import React, { memo } from 'react';
import { Trophy, TrendingUp, TrendingDown, Minus, AlertCircle } from 'lucide-react';

const StandingsTable = memo(({ standings, loading, error, mobile = false }) => {
  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
        <AlertCircle size={24} className="mx-auto mb-2 text-red-400" />
        <p className="text-red-300">{error}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(10)].map((_, i) => (
          <div key={i} className="bg-white/[0.03] rounded-lg p-4 animate-pulse">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/10 rounded"></div>
                <div className="w-10 h-10 bg-white/10 rounded-full"></div>
                <div className="w-32 h-4 bg-white/10 rounded"></div>
              </div>
              <div className="flex gap-4">
                {[...Array(4)].map((_, j) => (
                  <div key={j} className="w-8 h-4 bg-white/10 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!standings || standings.length === 0) {
    return (
      <div className="text-center py-12">
        <Trophy className="mx-auto mb-4 text-white/40" size={48} />
        <p className="text-white/70 text-lg">No standings data available</p>
        <p className="text-white/50 text-sm mt-2">Select a league and season to view standings</p>
      </div>
    );
  }

  const getPositionStyles = (position) => {
    if (position <= 4) return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30';
    if (position <= 6) return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
    if (position >= standings.length - 2) return 'bg-red-500/20 text-red-300 border-red-500/30';
    return 'bg-white/10 text-white/70 border-white/10';
  };

  const getFormIcon = (result) => {
    switch(result) {
      case 'W': return <div className="w-5 h-5 bg-emerald-500 rounded text-white text-xs font-bold flex items-center justify-center">W</div>;
      case 'D': return <div className="w-5 h-5 bg-yellow-500 rounded text-white text-xs font-bold flex items-center justify-center">D</div>;
      case 'L': return <div className="w-5 h-5 bg-red-500 rounded text-white text-xs font-bold flex items-center justify-center">L</div>;
      default: return null;
    }
  };

  return (
    <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
      <table className="w-full min-w-[500px]">
        <thead>
          <tr className="text-white/60 text-xs">
            <th className="text-left py-2 px-1 font-medium sticky left-0 bg-[#050311]/95 z-10" style={{ position: '-webkit-sticky' }}>#</th>
            <th className="text-left py-2 px-2 font-medium sticky left-9 bg-[#050311]/95 z-10" style={{ position: '-webkit-sticky' }}>Team</th>
            <th className="text-center py-2 px-1 font-medium">P</th>
            <th className="text-center py-2 px-1 font-medium">W</th>
            <th className="text-center py-2 px-1 font-medium">D</th>
            <th className="text-center py-2 px-1 font-medium">L</th>
            <th className="text-center py-2 px-1 font-medium">GF</th>
            <th className="text-center py-2 px-1 font-medium">GA</th>
            <th className="text-center py-2 px-1 font-medium">GD</th>
            <th className="text-center py-2 px-1 font-medium">Pts</th>
            {!mobile && <th className="text-center py-2 px-1 font-medium">Form</th>}
          </tr>
        </thead>
        <tbody>
          {standings.map((team, index) => (
            <tr 
              key={team.team.id}
              className="border-t border-white/[0.05] hover:bg-white/[0.02] transition-colors"
            >
              <td className="py-2 px-1 sticky left-0 bg-[#050311]/95 z-10" style={{ position: '-webkit-sticky' }}>
                <div className={`w-7 h-7 rounded border flex items-center justify-center font-bold text-xs ${getPositionStyles(team.rank)}`}>
                  {team.rank}
                </div>
              </td>
              <td className="py-2 px-2 sticky left-9 bg-[#050311]/95 z-10" style={{ position: '-webkit-sticky' }}>
                <div className="flex items-center gap-2">
                  <img 
                    src={team.team.logo} 
                    alt={team.team.name}
                    className="w-6 h-6 object-contain flex-shrink-0"
                    loading="lazy"
                  />
                  <span className="font-medium text-white text-sm truncate max-w-[120px]">{team.team.name}</span>
                </div>
              </td>
              <td className="text-center py-2 px-1 text-white/80 text-xs">{team.all.played}</td>
              <td className="text-center py-2 px-1 text-emerald-300 text-xs font-medium">{team.all.win}</td>
              <td className="text-center py-2 px-1 text-yellow-300 text-xs font-medium">{team.all.draw}</td>
              <td className="text-center py-2 px-1 text-red-300 text-xs font-medium">{team.all.lose}</td>
              <td className="text-center py-2 px-1 text-white/80 text-xs">{team.all.goals.for}</td>
              <td className="text-center py-2 px-1 text-white/80 text-xs">{team.all.goals.against}</td>
              <td className="text-center py-2 px-1">
                <span className={`font-bold text-xs ${team.goalsDiff > 0 ? 'text-emerald-300' : team.goalsDiff < 0 ? 'text-red-300' : 'text-white/60'}`}>
                  {team.goalsDiff > 0 && '+'}{team.goalsDiff}
                </span>
              </td>
              <td className="text-center py-2 px-1">
                <span className="font-black text-sm text-white">{team.points}</span>
              </td>
              {!mobile && (
                <td className="text-center py-2 px-1">
                  <div className="flex items-center justify-center gap-1">
                    {team.form?.split('').slice(-5).map((result, i) => (
                      <div key={i}>{getFormIcon(result)}</div>
                    ))}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

StandingsTable.displayName = 'StandingsTable';

export default StandingsTable;